import BulkAdjustType = ToroSaEnums.BulkAdjustType;
import MessageSeverity = ToroEnums.MessageSeverity;
import SaZoneType = ToroSaEnums.SaZoneType;

import { AppliedSaTargetProfile, SaTargetProfile } from './models/sa-target-profile.model';
import { Observable, of } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppInjector } from '../../demo/demo.module';
import { AuthManagerService } from '../auth/auth-manager.service';
import { BroadcastService } from '../../common/services/broadcast.service';
import { DashMessageService } from '../../common/services/dash-message.service';
import { DemoModeMockDataService } from '../../demo/demo-mode-mock-data.service';
import { environment } from '../../../environments/environment';
import { filter } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { SaEntityVwcHistory } from './models/sa-entity-vwc-history.model';
import { SaQueuedOperation } from './models/sa-background-operation.model';
import { SaSuggestedPercentAdjust } from './models/sa-suggested-percent-adjust.model';
import { SaUpdateTargetVwcResponse } from './models/sa-data.model';
import { SaUpdateZoneChartSelection } from './models/sa-update-zone-chart-selection.model';
import { SaVwcHistoryRequest } from './models/sa-vwc-history-request.model';
import { SoilFactorUpdate } from '../../spatial-adjust/components/sa-soil-factor-dlg/models/soil-factor-update';
import { SpatialAdjustApiService } from './spatial-adjust-api.service';
import { SpatialAdjustCourse } from './models/sa-course.model';
import { SpatialAdjustSettings } from '../site/models/spatial-adjust-settings.model';
import { ToroEnums } from '../../common/enumerations/toro.enums';
import { ToroSaEnums } from '../../spatial-adjust/enums/spatial-adjust.enums';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class SpatialAdjustManagerService {
    private demoModeMockDataService: DemoModeMockDataService;

    // Cache
    private selectedTurfRadCourseId: number = null;

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(private authManager: AuthManagerService,
                private broadcastService: BroadcastService,
                private dashMessageService: DashMessageService,
                private spatialAdjustApiService: SpatialAdjustApiService,
                private translate: TranslateService,
    ) {
        if (environment.isDemoMode) {
            this.demoModeMockDataService = AppInjector.get(DemoModeMockDataService);
        }

        this.broadcastService.saCourseSelectionChange
            .pipe(
                filter((course: SpatialAdjustCourse) => course != null),  // Filter out null values which indicate a reset rather than a selection.
                untilDestroyed(this))
            .subscribe((course: SpatialAdjustCourse) => {
                this.selectedTurfRadCourseId = course.turfRadCourseId;
            });
    }

    // =========================================================================================================================================================
    // Public Methods
    // =========================================================================================================================================================

    // getDataFromTurfRad(orgId: number, fromDate: string, page?: number): Observable<any> {
    //     return this.spatialAdjustApiService.getDataFromTurfRad(orgId, fromDate, page);
    // }

    // Returns the currently selected TurfRadCourseId.
    get turfRadCourseId(): number | null {
        return this.selectedTurfRadCourseId;
    }

    getCourses(orgId: number): Observable<SpatialAdjustCourse[]> {
        // Spatial Adjust Demo Support.
        if (environment.isDemoMode) { return of([]); }

        return this.spatialAdjustApiService.getCourses(orgId);
    }

    getData(orgId: number, turfRadCourseId: number = null): Observable<any> {
        // Spatial Adjust Demo Support.
        if (environment.isDemoMode) { return this.demoModeMockDataService.spatialAdjustAreasData;}

        return this.spatialAdjustApiService.getData(orgId, turfRadCourseId);
    }

    getTodayEt(orgId: number): Observable<number> {
        // Spatial Adjust Demo Support.
        if (environment.isDemoMode) { return this.demoModeMockDataService.spatialAdjustTodayEt; }

        return this.spatialAdjustApiService.getTodayEt(orgId);
    }

    // Retrieve data for Chart
    getVwcHistory(vwcHistoryRequest: SaVwcHistoryRequest): Observable<SaEntityVwcHistory[]> {
        // Spatial Adjust Demo Support.
        if (environment.isDemoMode) { return of(this.demoModeMockDataService.getSpatialAdjustVwcHistory(vwcHistoryRequest)); }

        return this.spatialAdjustApiService.getVwcHistory(new SaVwcHistoryRequest({...vwcHistoryRequest, turfRadCourseId: this.selectedTurfRadCourseId}));
    }

    // Retrieve Algo2 Suggested Percent Adjustments by Station for OrgId.
    getSuggestedPercentAdjustments(orgId: number, turfRadCourseId: number = null): Observable<SaSuggestedPercentAdjust[]> {
        // Spatial Adjust Demo Support.
        if (environment.isDemoMode) { return this.demoModeMockDataService.spatialAdjustSuggestedPctAdjustments; }

        return this.spatialAdjustApiService.getSuggestedPercentAdjustments(orgId, turfRadCourseId);
    }

    recalculateAdjustments(orgId: number, turfRacCourseId: number, settings: SpatialAdjustSettings): Observable<SaSuggestedPercentAdjust[]> {
        if (environment.isDemoMode) {
            this.showUnsupportedMsg();
            return;
        }

        return this.spatialAdjustApiService.recalculateAdjustments(orgId, turfRacCourseId, settings);
    }

    updateTargetVwc(orgId: number, stationId: string, targetVwcPercent: number, actualVwc: number, currentPercentAdjust: number): Observable<SaUpdateTargetVwcResponse> {
        if (environment.isDemoMode) {
            this.showUnsupportedMsg();
            return;
        }

        return this.spatialAdjustApiService.updateTargetVwc(orgId, this.selectedTurfRadCourseId, stationId, targetVwcPercent, actualVwc, currentPercentAdjust);
    }

    bulkUpdateTargetVwc(orgId: number, turfRadCourseId: number, targetVwcPercent: number, adjustType: BulkAdjustType, correlationId?: string): Observable<any> {
        if (environment.isDemoMode) {
            this.showUnsupportedMsg();
            return;
        }

        return this.spatialAdjustApiService.bulkUpdateTargetVwc(orgId, turfRadCourseId, targetVwcPercent, adjustType, correlationId);
    }

    updateUserPercentAdjust(orgId: number, turfRadCourseId: number, stationId: string, userPercentAdjust: number): Observable<any> {
        if (environment.isDemoMode) {
            this.showUnsupportedMsg();
            return;
        }

        return this.spatialAdjustApiService.updateUserPercentAdjust(orgId, turfRadCourseId, stationId, userPercentAdjust);
    }

    updateAutoAdjust(orgId: number, turfRadCourseId: number, zoneId: string, zoneType: SaZoneType, isEnabled = false, isSelected = false): Observable<void> {
        if (environment.isDemoMode) {
            this.showUnsupportedMsg();
            return;
        }

        return this.spatialAdjustApiService.updateAutoAdjust(orgId, turfRadCourseId, zoneId, zoneType, isEnabled, isSelected);
    }

    setEntityEnabledState(zoneType: SaZoneType, zoneId: string, isEnabled: boolean, cascade: boolean = false): Observable<void> {
        if (environment.isDemoMode) {
            this.showUnsupportedMsg();
            return;
        }

        return this.spatialAdjustApiService.setEntityEnabledState(zoneType, zoneId, isEnabled, cascade);
    }

    updateChartSelection(updateDto: SaUpdateZoneChartSelection): Observable<any> {
        if (environment.isDemoMode) {
            this.showUnsupportedMsg();
            return;
        }

        return this.spatialAdjustApiService.updateChartSelection(updateDto);
    }

    toggleRowSelection(orgId: number, zoneId: string, zoneType: SaZoneType, isRowSelected): Observable<any> {
        if (environment.isDemoMode) {
            this.showUnsupportedMsg();
            return of(true);
        }

        return this.spatialAdjustApiService.toggleRowSelection(orgId, this.selectedTurfRadCourseId, zoneId, zoneType, isRowSelected);
    }

    isSpatialAdjustUser(userName: string): boolean {
        if (this.authManager?.dashAuthenticatedUser == null) { return false; }

        return this.authManager.dashAuthenticatedUser?.spatialAdjust && this.authManager.dashAuthenticatedUser?.turfRadId != null;
    }

    updateLastPushDate(stationIds: string[]): Observable<SaUpdateTargetVwcResponse> {
        if (environment.isDemoMode) {
            this.showUnsupportedMsg();
            return;
        }
        return this.spatialAdjustApiService.updateLastPushDate(stationIds);
    }

    getStationsPushedTodayCount(orgId: number, turfRadCourseId: number = null): Observable<number> {
        // TODO - Spatial Adjust Demo Support.
        if (environment.isDemoMode) { return of(this.demoModeMockDataService.spatialAdjustStationsPushedToday); }

        return this.spatialAdjustApiService.getStationsPushedTodayCount(orgId, turfRadCourseId);
    }

    updateCourseName(saCourse: SpatialAdjustCourse): Observable<void> {
        return this.spatialAdjustApiService.updateCourseName(saCourse);
    }

    getSelectedStationsCount(orgId: number, turfRadCourseId: number = null): Observable<number> {
        if (environment.isDemoMode) { return of(this.demoModeMockDataService.spatialAdjustBulkAdjustCount); }

        return this.spatialAdjustApiService.getSelectedStationsCount(orgId, this.selectedTurfRadCourseId);
    }

    updateSoilFactors(updates: SoilFactorUpdate[]): Observable<SaSuggestedPercentAdjust[]> {
        if (environment.isDemoMode) {
            this.showUnsupportedMsg();
            return of(null);
        }

        return this.spatialAdjustApiService.updateSoilFactors(updates);
    }

    // Request a refresh of the scan data from the server for the current user's site.
    requestScanDataRefresh(): Observable<any> {
        if (environment.isDemoMode) {
            this.showUnsupportedMsg();
            return of(null);
        }

        return this.spatialAdjustApiService.requestScanDataRefresh();
    }

    hasTurfRadAccess(orgId: number): Observable<boolean> {
        return this.spatialAdjustApiService.hasTurfRadAccess(orgId);
    }

    hasScanData(orgId: number, turfRadCourseId?: number): Observable<boolean> {
        return this.spatialAdjustApiService.hasScanData(orgId, turfRadCourseId);
    }

    getLastScanDate(orgId: number, turfRadCourseId?: number): Observable<string> {
        return this.spatialAdjustApiService.getLastScanDate(orgId, turfRadCourseId);
    }

    getTargetProfiles(orgId: number): Observable<SaTargetProfile[]> {
        // Spatial Adjust Demo Support.
        if (environment.isDemoMode) { return of([]); }

        return this.spatialAdjustApiService.getTargetProfiles(orgId);
    }

    getAppliedTargetProfiles(orgId: number): Observable<AppliedSaTargetProfile[]> {
        // Spatial Adjust Demo Support.
        if (environment.isDemoMode) { return of([]); }

        return this.spatialAdjustApiService.getAppliedTargetProfiles(orgId);
    }

    upsertTargetProfiles(orgId: number, profiles: SaTargetProfile[]): Observable<SaQueuedOperation> {
        return this.spatialAdjustApiService.upsertTargetProfiles(orgId, profiles);
    }


    // =========================================================================================================================================================
    // Public Helper Methods
    // =========================================================================================================================================================

    public showUnsupportedMsg(): void {
        this.dashMessageService.showMessage(
            this.translate.instant('SPECIAL_MSG.UNSUPPORTED_IN_DEMO_MODE'),
            this.translate.instant('SPECIAL_MSG.UNSUPPORTED_ACTION_IN_DEMO_MODE'),
            MessageSeverity.Info);
    }

}
